/**
 * ProcessSettingsPanel - Collapsible sidebar panel for per-shape laser process settings
 *
 * Features:
 * - Three tabs: Score | Engrave | Cut
 * - Common settings: Output toggle, Power, Speed, Passes
 * - Score/Cut: Kerf offset
 * - Engrave: Density (lines/cm), Scan mode (bi/uni-directional)
 * - Cut: Lower focus (gantry lasers only)
 */

import { useState, useRef, useEffect } from 'react';
import { Settings2, ChevronRight, ClipboardCopy, ClipboardPaste, Copy, PencilLine, Slice, ChevronDown, AlertTriangle } from 'lucide-react';
import type { TemplateShape, ShapeProcessSettings } from '@jigflow/shared';
import { useTemplateStore } from './useTemplateStore';
import type { LightSourceMode } from '@jigflow/shared/generators/xcsGenerator';

// Paint bucket icon (not in lucide, using custom SVG)
const PaintBucketIcon = ({ size = 14, className = '' }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/>
        <path d="m5 2 5 5"/>
        <path d="M2 13h15"/>
        <path d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/>
    </svg>
);

// Light source color mapping for styling
const LIGHT_SOURCE_COLORS: Record<LightSourceMode, { bg: string; text: string; hover: string }> = {
    'blue': { bg: 'bg-blue-600', text: 'text-white', hover: 'hover:bg-blue-700' },
    'red': { bg: 'bg-red-600', text: 'text-white', hover: 'hover:bg-red-700' },
    'fiber': { bg: 'bg-amber-600', text: 'text-white', hover: 'hover:bg-amber-700' },
    'uv': { bg: 'bg-violet-600', text: 'text-white', hover: 'hover:bg-violet-700' }
};

// Custom dropdown for laser source selection with proper color highlighting
interface LaserSourceDropdownProps {
    currentValue: LightSourceMode;
    options: { value: LightSourceMode; label: string; description: string }[];
    onChange: (value: LightSourceMode) => void;
    disabled?: boolean;
}

function LaserSourceDropdown({ currentValue, options, onChange, disabled }: LaserSourceDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentOption = options.find(o => o.value === currentValue) || options[0];
    const colors = LIGHT_SOURCE_COLORS[currentValue] || LIGHT_SOURCE_COLORS['blue'];

    return (
        <div className="relative flex items-center gap-1" ref={dropdownRef}>
            <span className="text-[10px] text-slate-500 dark:text-slate-400">Laser:</span>
            <button
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded transition ${colors.bg} ${colors.text} ${!disabled ? colors.hover : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                title={currentOption?.description || 'Laser source'}
            >
                <span>{currentOption?.label || currentValue}</span>
                <ChevronDown size={10} />
            </button>

            {isOpen && !disabled && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-600 p-1 min-w-[80px]">
                    {options.map(opt => {
                        const optColors = LIGHT_SOURCE_COLORS[opt.value] || LIGHT_SOURCE_COLORS['blue'];
                        const isSelected = opt.value === currentValue;

                        return (
                            <button
                                key={opt.value}
                                onClick={() => {
                                    onChange(opt.value);
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] transition ${
                                    isSelected
                                        ? `${optColors.bg} ${optColors.text}`
                                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                                }`}
                                title={opt.description}
                            >
                                <span
                                    className={`w-3 h-3 rounded-full ${optColors.bg}`}
                                />
                                <span>{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Density values for engrave (lines per cm)
const DENSITY_VALUES = [
    10, 20, 30, 40, 50, 60, 70, 80, 90, 100,
    120, 140, 160, 180, 200, 220, 240, 260, 280, 300
];

interface ProcessSettingsPanelProps {
    shape: TemplateShape | null;
    processSettings: Map<string, ShapeProcessSettings>; // operation -> settings
    isExpanded: boolean;
    onToggleExpand: () => void;
    onSettingsChange: (operation: string, settings: Partial<ShapeProcessSettings>) => void;
    isGantryLaser: boolean; // Show lower focus for P2, P2S, P3
    maxSpeed?: number; // Machine's max speed in mm/s (for validation)
    software?: string; // Machine software type (xtool, lightburn, generic)
    // Copy/paste callbacks
    onCopy: () => void;
    onCopyAll: () => void;
    onPaste: () => void;
    canPaste: boolean;
    // Apply to all shapes with same operation
    onApplyToAll: () => void;
    operationColor: string; // Color for the current operation (for Apply to All button)
    // Light source selection (for dual-laser machines)
    showLightSource: boolean;
    lightSourceOptions: { value: LightSourceMode; label: string; description: string }[];
    // Per-operation light source from settings
    onLightSourceChange: (lightSource: LightSourceMode) => void;
}

type TabType = 'score' | 'engrave' | 'cut';

export default function ProcessSettingsPanel({
    shape,
    processSettings,
    isExpanded,
    onToggleExpand,
    onSettingsChange,
    isGantryLaser,
    maxSpeed = 600,
    software,
    onCopy,
    onCopyAll,
    onPaste,
    canPaste,
    onApplyToAll,
    operationColor,
    showLightSource,
    lightSourceOptions,
    onLightSourceChange
}: ProcessSettingsPanelProps) {
    // LightBurn doesn't have a separate score operation (score = cut)
    const isLightBurn = software === 'lightburn';
    const showScoreTab = !isLightBurn;
    const availableTabs: TabType[] = showScoreTab ? ['score', 'engrave', 'cut'] : ['engrave', 'cut'];

    // LightBurn uses mm/min for speed display, XCS uses mm/s
    // Internally we store mm/s, so convert for LightBurn display
    const speedMultiplier = isLightBurn ? 60 : 1;
    const speedUnit = isLightBurn ? 'mm/min' : 'mm/s';
    const maxSpeedDisplay = maxSpeed * speedMultiplier;
    // Use store for active tab so parent toolbar can access it
    const { activeProcessTab: activeTab, setActiveProcessTab: setActiveTab } = useTemplateStore();

    // Local state for speed number input (so user can finish typing before conversion)
    const [speedInputValue, setSpeedInputValue] = useState('');
    const [isEditingSpeed, setIsEditingSpeed] = useState(false);

    // Get current settings for active tab
    const currentSettings = processSettings.get(activeTab);

    // Check if operation is enabled on the shape
    const isOperationEnabled = (op: TabType): boolean => {
        if (!shape) return false;
        if (op === 'score') return shape.score === true;
        if (op === 'engrave') return shape.engrave !== false; // default true
        if (op === 'cut') return shape.cut === true;
        return false;
    };

    // Auto-switch to first enabled tab when shape changes
    useEffect(() => {
        if (shape) {
            // For LightBurn, skip score tab
            if (showScoreTab && isOperationEnabled('score')) {
                setActiveTab('score');
            } else if (isOperationEnabled('engrave')) {
                setActiveTab('engrave');
            } else if (isOperationEnabled('cut')) {
                setActiveTab('cut');
            }
        }
    }, [shape?.id, showScoreTab]);

    const handleToggleOutput = () => {
        const current = currentSettings?.outputEnabled ?? true;
        onSettingsChange(activeTab, { outputEnabled: !current });
    };

    const handlePowerChange = (value: number) => {
        onSettingsChange(activeTab, { power: Math.max(0, Math.min(100, value)) });
    };

    const handleSpeedChange = (displayValue: number) => {
        // Convert from display units (mm/min for LightBurn) to storage units (mm/s)
        const storageValue = displayValue / speedMultiplier;
        // Clamp speed between 1 mm/s and machine's maxSpeed
        const clampedSpeed = Math.max(1, Math.min(storageValue, maxSpeed));
        onSettingsChange(activeTab, { speed: clampedSpeed });
    };

    // Check if current speed exceeds machine's max (for warning indicator)
    const isSpeedOverMax = (currentSettings?.speed ?? 0) > maxSpeed;

    // Convert stored speed (mm/s) to display speed (mm/min for LightBurn)
    const displaySpeed = (currentSettings?.speed ?? 100) * speedMultiplier;

    const handlePassesChange = (value: number) => {
        onSettingsChange(activeTab, { passes: Math.max(1, Math.round(value)) });
    };

    const handleKerfToggle = () => {
        const current = currentSettings?.kerfEnabled ?? false;
        onSettingsChange(activeTab, { kerfEnabled: !current });
    };

    const handleKerfDistanceChange = (value: number) => {
        onSettingsChange(activeTab, { kerfDistance: Math.max(-1, Math.min(1, value)) });
    };

    const handleDensityChange = (value: number) => {
        onSettingsChange(activeTab, { density: value });
    };

    const handleScanModeChange = (mode: 'zMode' | 'oneWay') => {
        onSettingsChange(activeTab, { scanMode: mode });
    };

    const handleLowerFocusToggle = () => {
        const current = currentSettings?.lowerFocusEnabled ?? false;
        onSettingsChange(activeTab, { lowerFocusEnabled: !current });
    };

    const handleLowerFocusValueChange = (value: 1 | 2) => {
        onSettingsChange(activeTab, { lowerFocusValue: value });
    };

    // Tab (break point) handlers for cut operations
    const handleTabsToggle = () => {
        const current = currentSettings?.enableBreakPoint ?? false;
        onSettingsChange(activeTab, { enableBreakPoint: !current });
    };

    const handleTabCountChange = (value: number) => {
        onSettingsChange(activeTab, { breakPointCount: Math.max(1, Math.min(20, value)) });
    };

    const handleTabSizeChange = (value: number) => {
        onSettingsChange(activeTab, { breakPointSize: Math.max(0.1, Math.min(10, value)) });
    };

    const handleTabModeChange = (mode: 'count' | 'distance') => {
        onSettingsChange(activeTab, { breakPointMode: mode });
    };

    const handleTabDistanceChange = (value: number) => {
        onSettingsChange(activeTab, { breakPointDistance: Math.max(1, Math.min(500, value)) });
    };

    const handleTabPowerChange = (value: number) => {
        onSettingsChange(activeTab, { breakPointPower: Math.max(0, Math.min(100, value)) });
    };

    // Collapsed state - just show the handle
    if (!isExpanded) {
        return (
            <div className="flex-shrink-0 flex items-stretch">
                <button
                    onClick={onToggleExpand}
                    className="w-6 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border-l border-slate-200 dark:border-slate-700 flex items-center justify-center transition"
                    title="Expand Process Settings"
                >
                    <Settings2 size={14} className="text-slate-500 dark:text-slate-400" />
                </button>
            </div>
        );
    }

    const tabEnabled = isOperationEnabled(activeTab);
    const outputEnabled = currentSettings?.outputEnabled ?? true;

    // Get current light source for this operation (from settings or default)
    const currentLightSource = currentSettings?.lightSource || 'blue';

    return (
        <div className="w-52 flex-shrink-0 flex border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            {/* Collapse handle */}
            <button
                onClick={onToggleExpand}
                className="w-6 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition flex-shrink-0"
                title="Collapse Process Settings"
            >
                <ChevronRight size={14} className="text-slate-500 dark:text-slate-400" />
            </button>

            {/* Panel content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header Row 1 - Copy/Paste/Apply buttons */}
                <div className="px-1.5 pt-1.5 pb-1 flex items-center gap-1">
                    {/* Copy button */}
                    <button
                        onClick={onCopy}
                        disabled={!shape || !processSettings.has(activeTab)}
                        className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                        title={`Copy ${activeTab} settings`}
                    >
                        <ClipboardCopy size={14} />
                    </button>
                    {/* Copy All button */}
                    <button
                        onClick={onCopyAll}
                        disabled={!shape || processSettings.size === 0}
                        className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Copy all operation settings"
                    >
                        <Copy size={14} />
                    </button>
                    {/* Paste button */}
                    <button
                        onClick={onPaste}
                        disabled={!shape || !canPaste}
                        className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Paste settings"
                    >
                        <ClipboardPaste size={14} />
                    </button>

                    <div className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />

                    {/* Apply to All button with operation icon on colored background */}
                    <button
                        onClick={onApplyToAll}
                        disabled={!shape || !processSettings.has(activeTab)}
                        className="flex items-center gap-1 px-1 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                        title={`Apply to all ${activeTab} shapes with this color`}
                    >
                        <span
                            className="w-5 h-5 rounded flex items-center justify-center"
                            style={{ backgroundColor: operationColor }}
                        >
                            {activeTab === 'score' ? <PencilLine size={12} className="text-white drop-shadow-sm" /> :
                             activeTab === 'engrave' ? <PaintBucketIcon size={12} className="text-white drop-shadow-sm" /> :
                             <Slice size={12} className="text-white drop-shadow-sm" />}
                        </span>
                        <span>All</span>
                    </button>
                </div>

                {/* Header Row 2 - Output toggle and Laser source */}
                <div className="px-1.5 pb-1.5 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                    {/* Output toggle */}
                    <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">Output:</span>
                        <button
                            onClick={handleToggleOutput}
                            disabled={!tabEnabled}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition ${
                                tabEnabled
                                    ? outputEnabled
                                        ? 'bg-green-500 text-white'
                                        : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-300 dark:text-slate-600 cursor-not-allowed'
                            }`}
                            title={outputEnabled && tabEnabled ? 'Output: ON' : 'Output: OFF'}
                        >
                            {outputEnabled && tabEnabled ? 'ON' : 'OFF'}
                        </button>
                    </div>

                    {/* Light source dropdown - only for dual-laser machines */}
                    {showLightSource && lightSourceOptions.length > 0 && (
                        <LaserSourceDropdown
                            currentValue={currentLightSource}
                            options={lightSourceOptions}
                            onChange={onLightSourceChange}
                            disabled={!tabEnabled}
                        />
                    )}
                </div>

                {!shape ? (
                    <div className="flex-1 flex items-center justify-center p-4">
                        <p className="text-xs text-slate-400 text-center">
                            Select a shape to configure process settings
                        </p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto">
                        {/* Tabs */}
                        <div className="flex border-b border-slate-200 dark:border-slate-700">
                            {availableTabs.map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`flex-1 py-2 text-xs font-medium transition relative ${
                                        activeTab === tab
                                            ? 'text-blue-600 dark:text-blue-400'
                                            : isOperationEnabled(tab)
                                                ? 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                                : 'text-slate-300 dark:text-slate-600'
                                    }`}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    {activeTab === tab && (
                                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Settings form */}
                        <div className={`p-2 space-y-3 ${!tabEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            {/* Power slider */}
                            <div>
                                <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                                    Power (%)
                                </label>
                                <div className="flex items-center gap-1.5">
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={currentSettings?.power ?? 50}
                                        onChange={(e) => handlePowerChange(parseInt(e.target.value))}
                                        className="flex-1 min-w-0 h-1 rounded-full appearance-none bg-slate-200 dark:bg-slate-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentSettings?.power ?? ''}
                                        onChange={(e) => handlePowerChange(parseInt(e.target.value) || 0)}
                                        placeholder="--"
                                        className="w-10 px-1 py-0.5 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                </div>
                            </div>

                            {/* Speed slider */}
                            <div>
                                <div className="flex items-center gap-1 mb-1">
                                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                                        Speed ({speedUnit})
                                    </label>
                                    {isSpeedOverMax && (
                                        <div className="flex items-center gap-0.5 text-amber-500" title={`Speed exceeds machine max (${maxSpeedDisplay} ${speedUnit})`}>
                                            <AlertTriangle size={10} />
                                            <span className="text-[9px]">exceeds max</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <input
                                        type="range"
                                        min={speedMultiplier}
                                        max={maxSpeedDisplay}
                                        step={speedMultiplier}
                                        value={Math.min(displaySpeed, maxSpeedDisplay)}
                                        onChange={(e) => handleSpeedChange(parseInt(e.target.value))}
                                        className="flex-1 min-w-0 h-1 rounded-full appearance-none bg-slate-200 dark:bg-slate-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                                    />
                                    <input
                                        type="number"
                                        min={speedMultiplier}
                                        max={maxSpeedDisplay}
                                        value={isEditingSpeed ? speedInputValue : (currentSettings?.speed ? Math.round(displaySpeed) : '')}
                                        onChange={(e) => {
                                            if (isEditingSpeed) {
                                                setSpeedInputValue(e.target.value);
                                            } else {
                                                handleSpeedChange(parseInt(e.target.value) || speedMultiplier);
                                            }
                                        }}
                                        onFocus={() => {
                                            setIsEditingSpeed(true);
                                            setSpeedInputValue(currentSettings?.speed ? String(Math.round(displaySpeed)) : '');
                                        }}
                                        onBlur={() => {
                                            setIsEditingSpeed(false);
                                            const val = parseInt(speedInputValue);
                                            if (!isNaN(val) && val > 0) handleSpeedChange(val);
                                        }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                        placeholder="--"
                                        className={`w-14 px-1 py-0.5 text-xs bg-white dark:bg-slate-700 border rounded text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                            isSpeedOverMax
                                                ? 'border-amber-400 dark:border-amber-500'
                                                : 'border-slate-200 dark:border-slate-600'
                                        }`}
                                    />
                                </div>
                            </div>

                            {/* Passes */}
                            <div>
                                <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                                    Passes
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={currentSettings?.passes ?? 1}
                                    onChange={(e) => handlePassesChange(parseInt(e.target.value) || 1)}
                                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>

                            {/* Air Assist (LightBurn only) */}
                            {isLightBurn && (
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">Air Assist</span>
                                    <button
                                        onClick={() => {
                                            // Default: OFF for engrave (spreads soot), ON for cut (safety)
                                            const defaultValue = activeTab === 'cut';
                                            const currentValue = currentSettings?.airAssist ?? defaultValue;
                                            onSettingsChange(activeTab, { airAssist: !currentValue });
                                        }}
                                        className={`px-2 py-0.5 text-[10px] font-medium rounded transition ${
                                            (currentSettings?.airAssist ?? (activeTab === 'cut'))
                                                ? 'bg-green-500 text-white'
                                                : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                                        }`}
                                    >
                                        {(currentSettings?.airAssist ?? (activeTab === 'cut')) ? 'ON' : 'OFF'}
                                    </button>
                                </div>
                            )}

                            {/* Z-Axis (LightBurn only) */}
                            {isLightBurn && (
                                <details className="border-t border-slate-200 dark:border-slate-600 pt-2 mt-2 group/zaxis">
                                    <summary className="flex items-center justify-between cursor-pointer text-[10px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 list-none [&::-webkit-details-marker]:hidden">
                                        <div className="flex items-center gap-1">
                                            <ChevronRight size={12} className="transition-transform group-open/zaxis:rotate-90" />
                                            <span>Z-Axis</span>
                                        </div>
                                    </summary>
                                    <div className="mt-2 space-y-2 ml-2">
                                        {/* Z Offset: -10 to +10 mm */}
                                        <div>
                                            <label className="block text-[10px] text-slate-400 mb-0.5">Z Offset (mm)</label>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="range"
                                                    min="-100"
                                                    max="100"
                                                    step="1"
                                                    value={(currentSettings?.zOffset ?? 0) * 10}
                                                    onChange={(e) => onSettingsChange(activeTab, { zOffset: parseInt(e.target.value) / 10 })}
                                                    className="flex-1 h-1 rounded-full appearance-none bg-slate-200 dark:bg-slate-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                                                />
                                                <span className="w-10 text-right text-[10px] text-slate-500 dark:text-slate-400">
                                                    {(currentSettings?.zOffset ?? 0).toFixed(1)}
                                                </span>
                                            </div>
                                        </div>
                                        {/* Z Per Pass: 0 to 5 mm */}
                                        <div>
                                            <label className="block text-[10px] text-slate-400 mb-0.5">Z Per Pass (mm)</label>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="50"
                                                    step="1"
                                                    value={(currentSettings?.zPerPass ?? 0) * 10}
                                                    onChange={(e) => onSettingsChange(activeTab, { zPerPass: parseInt(e.target.value) / 10 })}
                                                    className="flex-1 h-1 rounded-full appearance-none bg-slate-200 dark:bg-slate-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                                                />
                                                <span className="w-10 text-right text-[10px] text-slate-500 dark:text-slate-400">
                                                    {(currentSettings?.zPerPass ?? 0).toFixed(1)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </details>
                            )}

                            {/* Kerf offset (Score & Cut only) */}
                            {(activeTab === 'score' || activeTab === 'cut') && (
                                <div>
                                    <label className="flex items-center gap-1.5 mb-1">
                                        <input
                                            type="checkbox"
                                            checked={currentSettings?.kerfEnabled ?? false}
                                            onChange={handleKerfToggle}
                                            className="w-3 h-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                                            Kerf Offset
                                        </span>
                                    </label>
                                    {currentSettings?.kerfEnabled && (
                                        <div className="flex items-center gap-1 ml-4">
                                            <input
                                                type="range"
                                                min="-10"
                                                max="10"
                                                step="1"
                                                value={(currentSettings?.kerfDistance ?? 0) * 10}
                                                onChange={(e) => handleKerfDistanceChange(parseInt(e.target.value) / 10)}
                                                className="flex-1 min-w-0 h-1 rounded-full appearance-none bg-slate-200 dark:bg-slate-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                                            />
                                            <span className="text-[10px] text-slate-500 dark:text-slate-400 w-9 text-right flex-shrink-0">
                                                {(currentSettings?.kerfDistance ?? 0).toFixed(1)}mm
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Density (Engrave only) */}
                            {activeTab === 'engrave' && (
                                <div>
                                    <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                                        Lines/cm
                                    </label>
                                    <select
                                        value={currentSettings?.density ?? 100}
                                        onChange={(e) => handleDensityChange(parseInt(e.target.value))}
                                        className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded"
                                    >
                                        {DENSITY_VALUES.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Scan mode (Engrave only) */}
                            {activeTab === 'engrave' && (
                                <div>
                                    <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                                        Scan Mode
                                    </label>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => handleScanModeChange('zMode')}
                                            className={`flex-1 py-1 text-[10px] rounded transition ${
                                                (currentSettings?.scanMode ?? 'zMode') === 'zMode'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                                            }`}
                                        >
                                            Bi-dir
                                        </button>
                                        <button
                                            onClick={() => handleScanModeChange('oneWay')}
                                            className={`flex-1 py-1 text-[10px] rounded transition ${
                                                currentSettings?.scanMode === 'oneWay'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                                            }`}
                                        >
                                            Uni-dir
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Advanced Fill (LightBurn engrave only) */}
                            {isLightBurn && activeTab === 'engrave' && (
                                <details className="border-t border-slate-200 dark:border-slate-600 pt-2 mt-2 group/advfill">
                                    <summary
                                        className="flex items-center justify-between cursor-pointer text-[10px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 list-none [&::-webkit-details-marker]:hidden"
                                        onClick={(e) => {
                                            // Auto-scroll to show expanded content
                                            const details = e.currentTarget.parentElement;
                                            if (details && !details.hasAttribute('open')) {
                                                setTimeout(() => details.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-1">
                                            <ChevronRight size={12} className="transition-transform group-open/advfill:rotate-90" />
                                            <span>Advanced Fill</span>
                                        </div>
                                    </summary>
                                    <div className="mt-2 space-y-2 ml-2">
                                        {/* Scan Angle */}
                                        <div>
                                            <label className="block text-[10px] text-slate-400 mb-0.5">Scan Angle</label>
                                            <select
                                                value={currentSettings?.scanAngle ?? 0}
                                                onChange={(e) => onSettingsChange('engrave', { scanAngle: parseFloat(e.target.value) })}
                                                className="w-full px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                                            >
                                                <option value={0}>0°</option>
                                                <option value={90}>90°</option>
                                                <option value={180}>180°</option>
                                                <option value={270}>270°</option>
                                                <option value={45}>45°</option>
                                                <option value={135}>135°</option>
                                            </select>
                                        </div>
                                        {/* Cross-Hatch */}
                                        <label className="flex items-center gap-1.5">
                                            <input
                                                type="checkbox"
                                                checked={currentSettings?.crossHatch ?? false}
                                                onChange={(e) => onSettingsChange('engrave', { crossHatch: e.target.checked })}
                                                className="w-3 h-3 rounded border-slate-300 text-blue-600"
                                            />
                                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Cross-Hatch (2-pass 90°)</span>
                                        </label>
                                        {/* Flood Fill */}
                                        <label className="flex items-center gap-1.5">
                                            <input
                                                type="checkbox"
                                                checked={currentSettings?.floodFill ?? false}
                                                onChange={(e) => onSettingsChange('engrave', { floodFill: e.target.checked })}
                                                className="w-3 h-3 rounded border-slate-300 text-blue-600"
                                            />
                                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Flood Fill</span>
                                        </label>
                                        {/* Overscan % */}
                                        <div>
                                            <label className="block text-[10px] text-slate-400 mb-0.5">Overscan (%)</label>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="250"
                                                    step="5"
                                                    value={(currentSettings?.overscanPercent ?? 2.5) * 10}
                                                    onChange={(e) => onSettingsChange('engrave', { overscanPercent: parseInt(e.target.value) / 10 })}
                                                    className="flex-1 h-1 rounded-full appearance-none bg-slate-200 dark:bg-slate-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
                                                />
                                                <span className="w-8 text-right text-[10px] text-slate-500 dark:text-slate-400">
                                                    {(currentSettings?.overscanPercent ?? 2.5).toFixed(1)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </details>
                            )}

                            {/* Lower focus (Cut only, gantry lasers) */}
                            {activeTab === 'cut' && isGantryLaser && (
                                <div>
                                    <label className="flex items-center gap-2 mb-1">
                                        <input
                                            type="checkbox"
                                            checked={currentSettings?.lowerFocusEnabled ?? false}
                                            onChange={handleLowerFocusToggle}
                                            className="w-3 h-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                                            Lower Focus
                                        </span>
                                    </label>
                                    {currentSettings?.lowerFocusEnabled && (
                                        <div className="flex gap-1 ml-5">
                                            <button
                                                onClick={() => handleLowerFocusValueChange(1)}
                                                className={`flex-1 py-1 text-[10px] rounded transition ${
                                                    (currentSettings?.lowerFocusValue ?? 1) === 1
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                                }`}
                                            >
                                                1mm
                                            </button>
                                            <button
                                                onClick={() => handleLowerFocusValueChange(2)}
                                                className={`flex-1 py-1 text-[10px] rounded transition ${
                                                    currentSettings?.lowerFocusValue === 2
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                                }`}
                                            >
                                                2mm
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Tabs / Break Points (Cut only) - for creating uncut sections */}
                            {activeTab === 'cut' && (
                                <div className="border-t border-slate-200 dark:border-slate-600 pt-2 mt-2">
                                    <label className="flex items-center gap-2 mb-2">
                                        <input
                                            type="checkbox"
                                            checked={currentSettings?.enableBreakPoint ?? false}
                                            onChange={handleTabsToggle}
                                            className="w-3 h-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                                            Tabs (hold material in place)
                                        </span>
                                    </label>
                                    {currentSettings?.enableBreakPoint && (
                                        <div className="ml-5 space-y-2">
                                            {/* Tab Mode */}
                                            <div>
                                                <label className="block text-[10px] text-slate-400 mb-1">
                                                    Generate
                                                </label>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleTabModeChange('count')}
                                                        className={`flex-1 py-1 text-[10px] rounded transition ${
                                                            (currentSettings?.breakPointMode ?? 'count') === 'count'
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                                        }`}
                                                    >
                                                        By Number
                                                    </button>
                                                    <button
                                                        onClick={() => handleTabModeChange('distance')}
                                                        className={`flex-1 py-1 text-[10px] rounded transition ${
                                                            currentSettings?.breakPointMode === 'distance'
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                                        }`}
                                                    >
                                                        By Spacing
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Count or Distance based on mode */}
                                            {(currentSettings?.breakPointMode ?? 'count') === 'count' ? (
                                                <div>
                                                    <label className="block text-[10px] text-slate-400 mb-1">
                                                        Number of Tabs
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="20"
                                                        value={currentSettings?.breakPointCount ?? 2}
                                                        onChange={(e) => handleTabCountChange(parseInt(e.target.value) || 2)}
                                                        className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    />
                                                </div>
                                            ) : (
                                                <div>
                                                    <label className="block text-[10px] text-slate-400 mb-1">
                                                        Tab Spacing (mm)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="500"
                                                        value={currentSettings?.breakPointDistance ?? 100}
                                                        onChange={(e) => handleTabDistanceChange(parseInt(e.target.value) || 100)}
                                                        className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    />
                                                </div>
                                            )}

                                            {/* Tab Size */}
                                            <div>
                                                <label className="block text-[10px] text-slate-400 mb-1">
                                                    Tab Size (mm)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0.1"
                                                    max="10"
                                                    step="0.1"
                                                    value={currentSettings?.breakPointSize ?? 0.5}
                                                    onChange={(e) => handleTabSizeChange(parseFloat(e.target.value) || 0.5)}
                                                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                />
                                            </div>

                                            {/* Tab Power */}
                                            <div>
                                                <label className="block text-[10px] text-slate-400 mb-1">
                                                    Tab Power (%)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={currentSettings?.breakPointPower ?? 0}
                                                    onChange={(e) => handleTabPowerChange(parseInt(e.target.value) || 0)}
                                                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Advanced Line (LightBurn cut only) */}
                            {isLightBurn && activeTab === 'cut' && (
                                <details className="border-t border-slate-200 dark:border-slate-600 pt-2 mt-2 group/advline">
                                    <summary
                                        className="flex items-center justify-between cursor-pointer text-[10px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 list-none [&::-webkit-details-marker]:hidden"
                                        onClick={(e) => {
                                            // Auto-scroll to show expanded content
                                            const details = e.currentTarget.parentElement;
                                            if (details && !details.hasAttribute('open')) {
                                                setTimeout(() => details.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-1">
                                            <ChevronRight size={12} className="transition-transform group-open/advline:rotate-90" />
                                            <span>Advanced Line</span>
                                        </div>
                                    </summary>
                                    <div className="mt-2 space-y-2 ml-2">
                                        {/* Cut-Through */}
                                        <div>
                                            <label className="flex items-center gap-1.5">
                                                <input
                                                    type="checkbox"
                                                    checked={currentSettings?.enableCutThrough ?? false}
                                                    onChange={(e) => onSettingsChange('cut', { enableCutThrough: e.target.checked })}
                                                    className="w-3 h-3 rounded border-slate-300 text-blue-600"
                                                />
                                                <span className="text-[10px] text-slate-500 dark:text-slate-400">Cut-Through</span>
                                            </label>
                                            {currentSettings?.enableCutThrough && (
                                                <div className="ml-4 mt-1">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-12">Power %</span>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            value={currentSettings?.cutThroughPower ?? 100}
                                                            onChange={(e) => onSettingsChange('cut', { cutThroughPower: parseFloat(e.target.value) })}
                                                            className="w-12 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Overcut */}
                                        <div className="flex items-center gap-1">
                                            <span className="text-[10px] text-slate-400 flex-1">Overcut (mm)</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                step="0.1"
                                                value={currentSettings?.overcut ?? 0}
                                                onChange={(e) => onSettingsChange('cut', { overcut: parseFloat(e.target.value) || 0 })}
                                                className="w-12 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            />
                                        </div>

                                        {/* Ramp Length */}
                                        <div className="flex items-center gap-1">
                                            <span className="text-[10px] text-slate-400 flex-1">Ramp (mm)</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="20"
                                                step="0.1"
                                                value={currentSettings?.rampLength ?? 0}
                                                onChange={(e) => onSettingsChange('cut', { rampLength: parseFloat(e.target.value) || 0 })}
                                                className="w-12 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            />
                                        </div>

                                        {/* Perforation */}
                                        <div>
                                            <label className="flex items-center gap-1.5">
                                                <input
                                                    type="checkbox"
                                                    checked={currentSettings?.enablePerforation ?? false}
                                                    onChange={(e) => onSettingsChange('cut', { enablePerforation: e.target.checked })}
                                                    className="w-3 h-3 rounded border-slate-300 text-blue-600"
                                                />
                                                <span className="text-[10px] text-slate-500 dark:text-slate-400">Perforation</span>
                                            </label>
                                            {currentSettings?.enablePerforation && (
                                                <div className="ml-4 mt-1 space-y-1">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-12">Cut mm</span>
                                                        <input
                                                            type="number"
                                                            min="0.1"
                                                            max="50"
                                                            step="0.1"
                                                            value={currentSettings?.perforationCutLength ?? 2}
                                                            onChange={(e) => onSettingsChange('cut', { perforationCutLength: parseFloat(e.target.value) })}
                                                            className="w-12 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-12">Skip mm</span>
                                                        <input
                                                            type="number"
                                                            min="0.1"
                                                            max="50"
                                                            step="0.1"
                                                            value={currentSettings?.perforationSkipLength ?? 2}
                                                            onChange={(e) => onSettingsChange('cut', { perforationSkipLength: parseFloat(e.target.value) })}
                                                            className="w-12 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Dot Mode */}
                                        <div>
                                            <label className="flex items-center gap-1.5">
                                                <input
                                                    type="checkbox"
                                                    checked={currentSettings?.enableDotMode ?? false}
                                                    onChange={(e) => onSettingsChange('cut', { enableDotMode: e.target.checked })}
                                                    className="w-3 h-3 rounded border-slate-300 text-blue-600"
                                                />
                                                <span className="text-[10px] text-slate-500 dark:text-slate-400">Dot Mode</span>
                                            </label>
                                            {currentSettings?.enableDotMode && (
                                                <div className="ml-4 mt-1 space-y-1">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-12">Time ms</span>
                                                        <input
                                                            type="number"
                                                            min="0.1"
                                                            max="100"
                                                            step="0.1"
                                                            value={currentSettings?.dotTime ?? 1}
                                                            onChange={(e) => onSettingsChange('cut', { dotTime: parseFloat(e.target.value) })}
                                                            className="w-12 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-12">Space mm</span>
                                                        <input
                                                            type="number"
                                                            min="0.1"
                                                            max="10"
                                                            step="0.1"
                                                            value={currentSettings?.dotSpacing ?? 0.5}
                                                            onChange={(e) => onSettingsChange('cut', { dotSpacing: parseFloat(e.target.value) })}
                                                            className="w-12 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Lead In/Out */}
                                        <div>
                                            <label className="flex items-center gap-1.5">
                                                <input
                                                    type="checkbox"
                                                    checked={(currentSettings?.leadInType && currentSettings.leadInType !== 'none') ?? false}
                                                    onChange={(e) => onSettingsChange('cut', {
                                                        leadInType: e.target.checked ? 'line' : 'none',
                                                        leadOutType: e.target.checked ? 'line' : 'none'
                                                    })}
                                                    className="w-3 h-3 rounded border-slate-300 text-blue-600"
                                                />
                                                <span className="text-[10px] text-slate-500 dark:text-slate-400">Lead In/Out</span>
                                            </label>
                                            {currentSettings?.leadInType && currentSettings.leadInType !== 'none' && (
                                                <div className="ml-4 mt-1 space-y-1.5">
                                                    {/* Lead In Row */}
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-6">In:</span>
                                                        <select
                                                            value={currentSettings?.leadInType ?? 'line'}
                                                            onChange={(e) => onSettingsChange('cut', { leadInType: e.target.value as 'line' | 'arc' })}
                                                            className="w-12 px-0.5 py-0.5 text-[9px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                                                        >
                                                            <option value="line">Line</option>
                                                            <option value="arc">Arc</option>
                                                        </select>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="20"
                                                            step="0.1"
                                                            value={currentSettings?.leadInLength ?? 2}
                                                            onChange={(e) => onSettingsChange('cut', { leadInLength: parseFloat(e.target.value) })}
                                                            className="w-10 px-0.5 py-0.5 text-[9px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            title="Length (mm)"
                                                        />
                                                        <input
                                                            type="number"
                                                            min="-180"
                                                            max="180"
                                                            step="5"
                                                            value={currentSettings?.leadInAngle ?? 45}
                                                            onChange={(e) => onSettingsChange('cut', { leadInAngle: parseFloat(e.target.value) })}
                                                            className="w-10 px-0.5 py-0.5 text-[9px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            title="Angle (deg)"
                                                        />
                                                    </div>
                                                    {/* Lead Out Row */}
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-6">Out:</span>
                                                        <select
                                                            value={currentSettings?.leadOutType ?? 'line'}
                                                            onChange={(e) => onSettingsChange('cut', { leadOutType: e.target.value as 'line' | 'arc' })}
                                                            className="w-12 px-0.5 py-0.5 text-[9px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                                                        >
                                                            <option value="line">Line</option>
                                                            <option value="arc">Arc</option>
                                                        </select>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="20"
                                                            step="0.1"
                                                            value={currentSettings?.leadOutLength ?? 2}
                                                            onChange={(e) => onSettingsChange('cut', { leadOutLength: parseFloat(e.target.value) })}
                                                            className="w-10 px-0.5 py-0.5 text-[9px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            title="Length (mm)"
                                                        />
                                                        <input
                                                            type="number"
                                                            min="-180"
                                                            max="180"
                                                            step="5"
                                                            value={currentSettings?.leadOutAngle ?? 45}
                                                            onChange={(e) => onSettingsChange('cut', { leadOutAngle: parseFloat(e.target.value) })}
                                                            className="w-10 px-0.5 py-0.5 text-[9px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            title="Angle (deg)"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Frequency Override */}
                                        <div>
                                            <label className="flex items-center gap-1.5">
                                                <input
                                                    type="checkbox"
                                                    checked={currentSettings?.overrideFrequency ?? false}
                                                    onChange={(e) => onSettingsChange('cut', { overrideFrequency: e.target.checked })}
                                                    className="w-3 h-3 rounded border-slate-300 text-blue-600"
                                                />
                                                <span className="text-[10px] text-slate-500 dark:text-slate-400">Frequency Override</span>
                                            </label>
                                            {currentSettings?.overrideFrequency && (
                                                <div className="ml-4 mt-1">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[9px] text-slate-400 w-8">Hz</span>
                                                        <input
                                                            type="number"
                                                            min="1000"
                                                            max="100000"
                                                            step="1000"
                                                            value={currentSettings?.frequency ?? 20000}
                                                            onChange={(e) => onSettingsChange('cut', { frequency: parseInt(e.target.value) })}
                                                            className="w-16 px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </details>
                            )}
                        </div>

                        {/* Disabled message */}
                        {!tabEnabled && (
                            <div className="p-2 text-center">
                                <p className="text-[10px] text-slate-400 italic">
                                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} is not enabled for this shape
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Export the active tab type for use in parent components
export type { TabType };
